import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../../api/client'
import type { VpnSite, VpnSiteList } from '../../api/types'
import {
  LabelMediaPicker,
  LabelPageStyle,
  RollPrintHint,
  labelSheetClass,
  useFitToLabel,
  useLabelMedia
} from '../../components/LabelPrint'
import { ErrorBox, Loading } from '../../components/ui'

/**
 * Mærkatet til routeren på et VPN-site (beslutning 106): sitets navn, VPN-adressen og
 * LAN-adresserne - det, montøren står og mangler foran skabet. Printes på Brother QL
 * med 62 mm endeløs rulle, som enhedernes mærkater.
 *
 * <b>Ingen nøgler og ingen koder på mærkatet</b> - kun adresser, som ikke åbner noget i sig
 * selv. Adgangen afhænger stadig af gatewayen (beslutning 091).
 */
export function VpnLabelPage() {
  const { id } = useParams()
  const siteId = Number(id)
  const sites = useQuery({ queryKey: ['vpn-sites'], queryFn: () => api.get<VpnSiteList>('/api/vpn/sites') })
  const [copies, setCopies] = useState(1)
  const [media, setMedia] = useLabelMedia()

  useEffect(() => {
    const previous = document.title
    document.title = 'VPN-mærkat'
    return () => {
      document.title = previous
    }
  }, [])

  if (sites.isLoading) return <Loading />
  if (sites.error) return <ErrorBox error={sites.error} />

  const site = sites.data?.sites.find((s) => s.id === siteId)
  if (!site) return <ErrorBox error={new Error('Sitet findes ikke.')} />

  return (
    <>
      <div className="row no-print" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <Link className="btn btn--sm" to="/admin/vpn">
          Tilbage
        </Link>

        <label className="row small muted" style={{ gap: 6 }}>
          Kopier
          <input
            type="number"
            min={1}
            max={10}
            style={{ width: 70 }}
            value={copies}
            onChange={(event) => setCopies(Math.min(10, Math.max(1, Number(event.target.value) || 1)))}
          />
        </label>

        <LabelMediaPicker media={media} onChange={setMedia} />

        <button className="btn btn--primary btn--sm" onClick={() => window.print()}>
          Print
        </button>

        {!site.vpnAddress && (
          <span className="tiny muted">Gatewayen svarer ikke lige nu, så VPN-adressen mangler på mærkatet.</span>
        )}
      </div>

      <LabelPageStyle media={media} />
      <RollPrintHint media={media} />

      <div className={labelSheetClass(media)}>
        {Array.from({ length: copies }, (_, copy) => (
          <VpnLabel key={copy} site={site} media={media} />
        ))}
      </div>
    </>
  )
}

function VpnLabel({ site, media }: { site: VpnSite; media: string }) {
  // Er der mere tekst end plads, skaleres den ned, så den nederste linje altid kommer med.
  const body = useRef<HTMLDivElement>(null)
  useFitToLabel(body, [site, media])

  return (
    <div className="vpn-label">
      <div ref={body}>
        <div className="vpn-label__name">{site.name}</div>
        <div className="vpn-label__customer">{site.customerName}</div>

        <Row label="VPN-IP" value={site.vpnAddress} />
        <Row label="LAN via VPN" value={site.lanPrefix} />
        <Row label="Kundens LAN" value={site.customerLan} />
        <Row label="Router LAN" value={site.routerLanAddress} />
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="vpn-label__row">
      <span>{label}</span>
      <code>{value || '-'}</code>
    </div>
  )
}
